import type { McpConnectorResponse } from '@common/api/mcpConnectorService';

export interface McpDisconnectConnectionIdentity {
    id: string;
    connectionId?: string;
    workspaceIntegrationId?: string;
    provider: string;
    connectionMode?: 'INTERNAL_APP' | 'MCP_SERVER';
}

export interface McpDisconnectConnectionModeSource {
    provider: string;
    connectionMode?: unknown;
    externalAccountType?: unknown;
    externalAccountId?: unknown;
}

const normalizeProvider = (provider: string) => provider
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

export const isMcpDisconnectProvider = (provider: string) => {
    const normalizedProvider = normalizeProvider(provider);
    return normalizedProvider === 'custom-mcp' || normalizedProvider === 'dingtalk';
};

const normalizeConnectionMetadata = (value: unknown) => String(value ?? '')
    .trim()
    .toUpperCase();

export const resolveMcpDisconnectConnectionMode = ({
    provider,
    connectionMode,
    externalAccountType,
    externalAccountId,
}: McpDisconnectConnectionModeSource): 'INTERNAL_APP' | 'MCP_SERVER' => {
    const normalizedProvider = normalizeProvider(provider);
    if (normalizedProvider === 'custom-mcp') {
        return 'MCP_SERVER';
    }

    if (normalizedProvider !== 'dingtalk') {
        return 'INTERNAL_APP';
    }

    const hasMcpConnectionMetadata = [connectionMode, externalAccountType]
        .some(value => normalizeConnectionMetadata(value) === 'MCP_SERVER');
    const hasMcpExternalIdentity = String(externalAccountId ?? '')
        .trim()
        .toLowerCase()
        .startsWith('mcp:');

    return hasMcpConnectionMetadata || hasMcpExternalIdentity
        ? 'MCP_SERVER'
        : 'INTERNAL_APP';
};

export const isMcpBackedDisconnectConnection = (
    connection: Pick<McpDisconnectConnectionIdentity, 'provider' | 'connectionMode'>,
): boolean => (
    isMcpDisconnectProvider(connection.provider)
    && connection.connectionMode === 'MCP_SERVER'
);

export const resolveMcpConnectorForDisconnect = (
    connectors: McpConnectorResponse[],
    connection: McpDisconnectConnectionIdentity,
): McpConnectorResponse | undefined => {
    const normalizedProvider = normalizeProvider(connection.provider);
    if (!isMcpBackedDisconnectConnection(connection)) {
        return undefined;
    }

    const integrationId = String(connection.workspaceIntegrationId || connection.id);
    const connectionId = String(connection.connectionId || connection.id);

    return connectors.find((connector) => {
        const connectorBelongsToProvider = normalizedProvider === 'dingtalk'
            ? connector.platform === 'DINGTALK'
            : connector.platform !== 'DINGTALK';
        if (!connectorBelongsToProvider) {
            return false;
        }

        return String(connector.integrationId ?? '') === integrationId
            || String(connector.connectorId) === connectionId;
    });
};
